import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, addMonths, subMonths } from "date-fns";
import { it } from "date-fns/locale";
import { PageLayout } from "../components/PageLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Clock,
  MapPin,
  User,
  ExternalLink,
  Trash2,
  CalendarPlus,
  Download,
  Video,
} from "lucide-react";

const API = `${window.location.origin}/api`;

const MyCalendarPage = () => {
  const [bookings, setBookings] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bookingsRes, sessionsRes] = await Promise.all([
        axios.get(`${API}/bookings/my`),
        axios.get(`${API}/sessions`)
      ]);
      setBookings(bookingsRes.data.filter((b) => b.status === "confirmed"));
      setAllSessions(sessionsRes.data);
    } catch (error) {
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings/my`);
      setBookings(response.data.filter((b) => b.status === "confirmed"));
    } catch (error) {
      toast.error("Errore nel caricamento delle prenotazioni");
    }
  };

  const handleCancelBooking = async () => {
    if (!bookingToCancel) return;

    try {
      await axios.delete(`${API}/bookings/${bookingToCancel.booking_id}`);
      toast.success("Prenotazione cancellata");
      fetchBookings();
    } catch (error) {
      toast.error("Errore durante la cancellazione");
    } finally {
      setCancelDialogOpen(false);
      setBookingToCancel(null);
    }
  };

  const generateGoogleCalendarUrl = (booking) => {
    const startDate = parseISO(`${booking.course_date}T${booking.course_time}`);
    const endDate = new Date(startDate.getTime() + 3 * 60 * 60 * 1000); // 3 hours later

    const params = new URLSearchParams({
      action: "TEMPLATE",
      text: booking.course_title,
      dates: `${format(startDate, "yyyyMMdd'T'HHmmss")}/${format(endDate, "yyyyMMdd'T'HHmmss")}`,
      details: `Docente: ${booking.instructor}\n\nODR Academy - Estetica Oncologica`,
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  const downloadICalFile = (booking) => {
    const startDate = parseISO(`${booking.course_date}T${booking.course_time}`);
    const endDate = new Date(startDate.getTime() + 3 * 60 * 60 * 1000);
    
    const formatICalDate = (date) => {
      return format(date, "yyyyMMdd'T'HHmmss");
    };

    const icalContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//ODR Academy//Corso//IT
BEGIN:VEVENT
UID:${booking.booking_id}@odracademy.it
DTSTAMP:${formatICalDate(new Date())}
DTSTART:${formatICalDate(startDate)}
DTEND:${formatICalDate(endDate)}
SUMMARY:${booking.course_title}
DESCRIPTION:Docente: ${booking.instructor}\\n\\nODR Academy - Estetica Oncologica
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icalContent], { type: "text/calendar;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `corso-${booking.course_title.replace(/\s+/g, "-").toLowerCase()}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success("File iCal scaricato!");
  };

  // Calendar rendering
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad days to start from Monday
  const startDay = monthStart.getDay();
  const paddingDays = startDay === 0 ? 6 : startDay - 1;

  const getBookingsForDate = (date) => {
    return bookings.filter((b) => isSameDay(parseISO(b.course_date), date));
  };

  const getSessionsForDate = (date) => {
    return allSessions.filter((s) => isSameDay(parseISO(s.date), date));
  };

  const isBookedSession = (sessionId) => {
    return bookings.some(b => b.course_id === sessionId);
  };

  const bookingsForSelectedDate = selectedDate ? getBookingsForDate(selectedDate) : [];
  const sessionsForSelectedDate = selectedDate ? getSessionsForDate(selectedDate) : [];

  return (
    <PageLayout>
      

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 md:py-12">
        <div className="mb-6 sm:mb-8">
          <h1 className="font-serif text-2xl sm:text-3xl md:text-4xl font-semibold text-foreground mb-1 sm:mb-2">
            Il Mio Calendario
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            Visualizza e gestisci le tue iscrizioni ai corsi
          </p>
        </div>

        {loading ? (
          <div className="animate-pulse space-y-4">
            <div className="h-64 sm:h-96 bg-secondary rounded-2xl" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {/* Calendar */}
            <div className="lg:col-span-2">
              <Card className="rounded-2xl border-border/50">
                <CardHeader className="flex flex-row items-center justify-between p-4 sm:p-6">
                  <CardTitle className="font-serif text-base sm:text-xl flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    <span className="capitalize">{format(currentMonth, "MMMM yyyy", { locale: it })}</span>
                  </CardTitle>
                  <div className="flex gap-1 sm:gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-full h-8 w-8 sm:h-10 sm:w-10"
                      onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                      data-testid="prev-month-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="rounded-full h-8 w-8 sm:h-10 sm:w-10"
                      onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                      data-testid="next-month-btn"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-2 sm:p-6">
                  {/* Weekday Headers */}
                  <div className="grid grid-cols-7 gap-0.5 sm:gap-1 mb-1 sm:mb-2">
                    {["L", "M", "M", "G", "V", "S", "D"].map((day, i) => (
                      <div key={i} className="text-center text-xs sm:text-sm font-medium text-muted-foreground py-1 sm:py-2">
                        <span className="sm:hidden">{day}</span>
                        <span className="hidden sm:inline">{["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"][i]}</span>
                      </div>
                    ))}
                  </div>

                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-0.5 sm:gap-1">
                    {/* Padding days */}
                    {Array.from({ length: paddingDays }).map((_, i) => (
                      <div key={`pad-${i}`} className="aspect-square" />
                    ))}

                    {/* Days of month */}
                    {daysInMonth.map((day) => {
                      const dayBookings = getBookingsForDate(day);
                      const daySessions = getSessionsForDate(day);
                      const hasBookings = dayBookings.length > 0;
                      const hasSessions = daySessions.length > 0;
                      const isSelected = selectedDate && isSameDay(day, selectedDate);
                      const isToday = isSameDay(day, new Date());

                      return (
                        <button
                          key={day.toISOString()}
                          onClick={() => setSelectedDate(day)}
                          className={`
                            aspect-square rounded-xl flex flex-col items-center justify-center relative
                            transition-all duration-200
                            ${isSelected ? "bg-primary text-primary-foreground shadow-lg scale-105" : "hover:bg-secondary"}
                            ${isToday && !isSelected ? "ring-2 ring-primary/30" : ""}
                            ${hasBookings && !isSelected ? "bg-green-100 border-2 border-green-400" : ""}
                            ${hasSessions && !hasBookings && !isSelected ? "bg-amber-50 border-2 border-amber-300" : ""}
                          `}
                          data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
                        >
                          <span className={`text-sm font-medium ${!isSameMonth(day, currentMonth) ? "text-muted-foreground" : ""} ${hasSessions || hasBookings ? "font-bold" : ""}`}>
                            {format(day, "d")}
                          </span>
                          {(hasSessions || hasBookings) && (
                            <div className={`absolute bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5`}>
                              {hasBookings ? (
                                <div className={`w-2.5 h-2.5 rounded-full ${isSelected ? "bg-primary-foreground" : "bg-green-500"}`} />
                              ) : (
                                <div className={`w-2.5 h-2.5 rounded-full ${isSelected ? "bg-primary-foreground" : "bg-amber-500"}`} />
                              )}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar - Selected Day Details or All Bookings */}
            <div className="space-y-6">
              {selectedDate ? (
                <Card className="rounded-2xl border-border/50">
                  <CardHeader>
                    <CardTitle className="font-serif text-lg">
                      {format(selectedDate, "EEEE d MMMM", { locale: it })}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {bookingsForSelectedDate.length > 0 ? (
                      <div className="space-y-4">
                        {bookingsForSelectedDate.map((booking) => (
                          <div
                            key={booking.booking_id}
                            className="p-4 bg-secondary/50 rounded-xl space-y-3"
                            data-testid={`booking-${booking.booking_id}`}
                          >
                            <h4 className="font-medium">{booking.course_title}</h4>
                            <div className="space-y-2 text-sm text-muted-foreground">
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                <span>{booking.course_time}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                <span>{booking.instructor}</span>
                              </div>
                            </div>
                            
                            {/* Zoom Meeting Button */}
                            {booking.zoom_link && (
                              <a
                                href={booking.zoom_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block"
                              >
                                <Button 
                                  size="sm" 
                                  className="w-full rounded-full bg-blue-600 hover:bg-blue-700"
                                  data-testid={`zoom-join-${booking.booking_id}`}
                                >
                                  <Video className="w-4 h-4 mr-2" />
                                  Partecipa al Meeting
                                </Button>
                              </a>
                            )}
                            
                            <div className="flex flex-col gap-2 pt-2">
                              <div className="flex gap-2">
                                <a
                                  href={generateGoogleCalendarUrl(booking)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex-1"
                                >
                                  <Button variant="outline" size="sm" className="w-full rounded-full" data-testid={`google-calendar-${booking.booking_id}`}>
                                    <CalendarPlus className="w-4 h-4 mr-1" />
                                    Google
                                  </Button>
                                </a>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="flex-1 rounded-full"
                                  onClick={() => downloadICalFile(booking)}
                                  data-testid={`ical-download-${booking.booking_id}`}
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  iCal
                                </Button>
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="rounded-full text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => {
                                  setBookingToCancel(booking);
                                  setCancelDialogOpen(true);
                                }}
                                data-testid={`cancel-booking-${booking.booking_id}`}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Annulla iscrizione
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : sessionsForSelectedDate.length > 0 ? (
                      <div className="space-y-4">
                        <p className="text-sm text-amber-600 font-medium flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                          Sessione disponibile
                        </p>
                        {sessionsForSelectedDate.map((session) => (
                          <div
                            key={session.session_id}
                            className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3"
                          >
                            <h4 className="font-medium">{session.title}</h4>
                            <div className="space-y-2 text-sm text-muted-foreground">
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                <span>{session.time} ({session.duration_hours}h)</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                <span>{session.docente_name}</span>
                              </div>
                            </div>
                            <Link to="/">
                              <Button size="sm" className="w-full rounded-full mt-2">
                                Iscriviti ora
                              </Button>
                            </Link>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground text-sm">Nessuna sessione per questo giorno</p>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <Card className="rounded-2xl border-border/50">
                  <CardHeader>
                    <CardTitle className="font-serif text-lg">Legenda</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded bg-green-100 border-2 border-green-400 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      </div>
                      <span className="text-sm">Sei già iscritta</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded bg-amber-50 border-2 border-amber-300 flex items-center justify-center">
                        <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                      </div>
                      <span className="text-sm">Sessione disponibile</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* My bookings card - always visible */}
              <Card className="rounded-2xl border-border/50">
                <CardHeader>
                  <CardTitle className="font-serif text-lg">Le tue iscrizioni</CardTitle>
                </CardHeader>
                <CardContent>
                  {bookings.length > 0 ? (
                    <div className="space-y-4">
                      {bookings
                        .sort((a, b) => new Date(a.course_date) - new Date(b.course_date))
                        .slice(0, 5)
                        .map((booking) => (
                          <div
                            key={booking.booking_id}
                            className="p-4 bg-green-50 border border-green-200 rounded-xl"
                          >
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="font-medium text-sm">{booking.course_title}</h4>
                              <Badge className="text-xs bg-green-500">
                                {format(parseISO(booking.course_date), "d MMM", { locale: it })}
                              </Badge>
                            </div>
                            <p className="text-xs text-muted-foreground">{booking.course_time} • {booking.instructor}</p>
                          </div>
                        ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
                      <p className="text-muted-foreground mb-4">Non hai ancora prenotazioni</p>
                      <Link to="/">
                        <Button className="rounded-full">Esplora le sessioni</Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* Cancel Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancella prenotazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicura di voler cancellare la prenotazione per "{bookingToCancel?.course_title}"?
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelBooking}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-cancel-btn"
            >
              Conferma cancellazione
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageLayout>
  );
};

export default MyCalendarPage;
